// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { findImprIdpDirectories } from '@api/commonServices';
import { Endpoints, StorageKeys } from '@api/constants';
import type { ImprDir } from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import type { AppError } from '@errorHandler/errors';
import { Button, InputBox } from '@imprivata-cloud/components';
import { Regex } from '@utils/utils';
import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import { STEPS } from '../../constants';
import { useAddImprIdpDirectory, useDirectoriesConfig } from '../../hooks/useImprivataIdp';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import { useGetPasswordPolicy } from '@/self-service/hooks/useGetPasswordPolicy';

import './ImprivataIdp.less';

const TRANSLATION_ROOT = `setup.${STEPS.IMPRIVATA_IDP}.content`;

const INPUT_CLASSNAME = 'impr-idp-input';
const SUBTITLE_CLASSNAME = 'impr-idp-subtitle';

export interface ImprIdpFormValues {
  domainName: string;
  username: string;
  password: string;
}

interface PasswordPolicy {
  minimumLength: number;
  maximumLength: number;
}

const ImprivataIdp = () => {
  const navigateToNextStep = useNavigateToNextStep();
  const { mutateAsync: addIdpDirectory } = useAddImprIdpDirectory(Endpoints.SETUP_IMPR_IDP_DIR_ADD);
  const [form] = Form.useForm<ImprIdpFormValues>();
  const { t } = useTranslation();
  const [domainName, setDomainName] = useState<string>();
  const [directory, setDirectory] = useState<ImprDir>();
  const { emitError } = useErrorContext();
  const { data: directoriesConfig } = useDirectoriesConfig(true);
  const imprDomainSuffix = directoriesConfig?.imprDomainSuffix
    ? `.${directoriesConfig.imprDomainSuffix}`
    : '';
  const [isLoading, setIsLoading] = useState(false);
  const { data: policyData } = useGetPasswordPolicy(true);
  const passwordPolicy: PasswordPolicy = policyData ? {
    minimumLength: policyData.minimumLength.value,
    maximumLength: policyData.maximumLength.value,
  } : {
    minimumLength: 15,
    maximumLength: 256,
  };

  function onSkipOrContinue() {
    navigateToNextStep(STEPS.EXTERNAL_IDP_INFO);
  }

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await findImprIdpDirectories(Endpoints.IMPR_IDP_DIR_FIND, true);
      if (response?.directories && response.directories.length > 0) {
        const directories = response.directories;
        if (directories[0].domains?.length > 0) {
          const rawDomain = directories[0].domains[0].domainName;
          const parsedDomain = rawDomain.split('.')[0];
          form.setFieldValue('domainName', parsedDomain);
          setDomainName(parsedDomain);
        }
        setDirectory(directories[0]);
        sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
      }
    } catch (error) {
      emitError(error as AppError);
    } finally {
      setIsLoading(false);
    }
  }, [form, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const values = form.getFieldsValue();
    if (!values) {
      return false;
    }
    return !(
      values.domainName?.length >= 3 &&
      values.username?.length >= 6 &&
      values.password?.length >= passwordPolicy.minimumLength &&
      values.password?.length <= passwordPolicy.maximumLength
    );
  };

  const onSubmit = async (values: ImprIdpFormValues) => {
    try {
      setIsLoading(true);
      const codingCtx = await createCodingCtxV2();
      const domainName = `${values?.domainName?.trim().toLowerCase().replaceAll('.', '-')}${imprDomainSuffix}`;
      const username = `${values?.username?.trim()}@${domainName}`;
      const password = codingCtx.encryptString(values?.password);

      // Retrieve business email from organization setup step
      const businessEmail = localStorage.getItem(StorageKeys.ORG_EMAIL);

      const payload = {
        addDirectoryRequest: {
          directory: {
            domains: [{ domainName: domainName, isPrimary: true }],
            directoryId: directory?.directoryId,
          },
          initialUser: {
            username: username,
            password: password,
            ...(businessEmail && { email: businessEmail }),
          },
        },
        codingContext: codingCtx,
      };

      await addIdpDirectory(payload, {
        onSuccess: () => {
          console.log('Imprivata IdP Directory added successfully:');
          sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
          onSkipOrContinue();
        },
      });
    } catch (error) {
      emitError(error as AppError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.subtitle`)}</div>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        aria-label={'impr-idp-form'}
        onFinish={onSubmit}
      >
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            name={'domainName'}
            rules={[
              { required: true },
              { pattern: Regex.imprIdPDomain, message: t(`${TRANSLATION_ROOT}.validation.domain`) },
              {
                pattern: Regex.imprIdPDomainHyphens,
                message: t(`${TRANSLATION_ROOT}.validation.domainHyphens`),
              },
              {
                pattern: Regex.imprIdPDomainHyphensPosition,
                message: t(`${TRANSLATION_ROOT}.validation.domainHyphensPosition`),
              },
            ]}
          >
            <InputBox
              autoFocus
              size="large"
              label={'Domain'}
              minLength={3}
              maxLength={63}
              disabled={!!directory?.domains[0]?.domainName}
              onChange={e => setDomainName(e.target.value?.replaceAll('.', '-'))}
            />
          </Form.Item>
          <span>{imprDomainSuffix}</span>
        </div>
        <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.acct-label`)} </div>
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            label={'Username'}
            name={'username'}
            rules={[
              { required: true },
              {
                pattern: Regex.imprIdPUsername,
                message: t(`${TRANSLATION_ROOT}.validation.username`),
              },
              {
                pattern: Regex.imprIdPUsernameTwoDots,
                message: t(`${TRANSLATION_ROOT}.validation.usernameTwoDots`),
              },
            ]}
          >
            <InputBox size="large" aria-label={'username'} minLength={6} maxLength={64} />
          </Form.Item>
          <span>{domainName ? `@${domainName}${imprDomainSuffix}` : ''}</span>
        </div>
        <div className={INPUT_CLASSNAME}>
          <Form.Item label={'Password'} name={'password'} rules={[{ required: true }]}>
            <InputBox
              size="large"
              aria-label={'password'}
              minLength={passwordPolicy.minimumLength}
              maxLength={passwordPolicy.maximumLength}
              type="password"
            />
          </Form.Item>
        </div>
        <p className={'impr-idp-sublabel'}>
          {t(`${TRANSLATION_ROOT}.password-hint`, { minimumLength: passwordPolicy.minimumLength, maximumLength: passwordPolicy.maximumLength })}
        </p>
        <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.info-text`)} </div>
        <Form.Item className={'impr-idp-continue-button'}>
          {/* Continue */}
          <ContinueButton
            disabled={isLoading}
            label={'Continue'}
            htmlType="submit"
            overrideErrorCheck={() => validateForm()}
            validateDirty={['username', 'password']}
          />
        </Form.Item>
        <Button
          label={t('common.skip')}
          onClick={onSkipOrContinue}
          size="large"
          className={'impr-idp-skip-button'}
        />
      </Form>
    </div>
  );
};

export default ImprivataIdp;
